import { Chip, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableFooter from "@material-ui/core/TableFooter";
import TableRow from "@material-ui/core/TableRow";
import EditIcon from "@material-ui/icons/Edit";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import ListIcon from "@material-ui/icons/List";
import { CSSProperties } from "@material-ui/styles";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { Coupons_coupons_edges_node } from "@saleor/coupons/types/Coupons";
import { checkCouponPermissionAccess } from "@saleor/coupons/utils";
import { User_userPermissions } from "@saleor/fragments/types/User";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { maybe, renderCollection } from "@saleor/misc";
import { StaffMemberDetails_user } from "@saleor/staff/types/StaffMemberDetails";
import { ListProps } from "@saleor/types";
import { CouponRedeemTypeEnum } from "@saleor/types/globalTypes";
import classNames from "classnames";
import React from "react";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";

import CouponFeaturedStar from "../CouponFeaturedStar";
import {
  CouponStatus,
  fiberIconColors,
  statusColors
} from "../CouponRankingList/style";

const useStyles = makeStyles(
  theme => {
    const overflowing: CSSProperties = {
      overflow: "hidden",
      textOverflow: "ellipsis"
    };
    return {
      assignedToContainer: {
        color: "#747C8C",
        fontSize: "0.8rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      colActions: {
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-around",
        padding: 0
      },
      colActionsDiv: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 10
      },
      colFeatured: {
        fontWeight: "bold",
        width: 100
      },
      colMedium: {
        overflowing,
        width: "12em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colNarrow: {
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colRow: {
        "& th": {
          fontWeight: 600 as 600,
          paddingBottom: theme.spacing(1),
          paddingTop: theme.spacing(1)
        }
      },
      colWide: {
        overflowing,
        width: "13em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeader: {
        fontWeight: "bold",
        overflowing,
        width: "10em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeaderCode: {
        fontWeight: "bold",
        overflowing,
        width: "15em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeaderMall: {
        fontWeight: "bold",
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideHeaderQty: {
        fontWeight: "bold",
        overflowing,
        width: "8em",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      colWideIcon: {
        overflowing,
        width: "100px",
        [theme.breakpoints.down("md")]: {
          width: "auto"
        }
      },
      couponCellCode: {
        backgroundColor: "#B4F2DF",
        borderRadius: "5px",
        color: "#2C7269",
        display: "inline-block",
        fontWeight: "bold",
        padding: "6px",
        textAlign: "center",
        width: "auto !important"
      },
      couponCellRedeemStatus: {
        color: "#000000",
        fontWeight: "bold"
      },
      couponDates: {
        fontSize: "12px"
      },
      createCouponButton: {
        backgroundColor: "#1848FE"
      },
      featuredStar: {
        marginLeft: "20px"
      },
      filters: {
        "& > *": {
          marginRight: theme.spacing(2)
        },
        alignItems: "center",
        display: "flex",
        padding: theme.spacing(2)
      },
      iconStatusCompleted: {
        color: "#63D5B6"
      },
      iconStatusEditing: {
        color: "#FECE21"
      },
      iconStatusOngoing: {
        color: "#5D7FFE"
      },
      redeemStatus: {
        fontSize: "14px"
      },
      redeemSubText: {
        fontSize: "10px"
      },
      statusCompleted: {
        backgroundColor: "#C2F2E9",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      statusEditing: {
        backgroundColor: "#FEF0BD",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      statusOngoing: {
        backgroundColor: "#BAC8FF",
        borderRadius: "30px",
        display: "inline-block",
        padding: "3px"
      },
      table: {
        tableLayout: "fixed"
      },
      tableCodes: {
        backgroundColor: "#B4F3DF",
        borderRadius: "5px",
        color: "#236C63",
        fontWeight: "bold",
        overflow: "hidden",
        padding: "6px",
        textAlign: "left",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "fit-content"
      },
      tableRow: {
        "& td": {
          wordBreak: "break-all"
        }
      },
      tableStatuses: {
        fontWeight: "bold"
      },
      tableTruncate: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      viewMallCell: {
        "&:hover": {
          color: "#0030FF",
          textDecoration: "underline"
        },
        color: "#247CFF",
        cursor: "pointer"
      }
    };
  },
  { name: "CouponsList" }
);

interface CouponsListProps extends ListProps {
  coupons?: Coupons_coupons_edges_node[];
  staffMember?: StaffMemberDetails_user;
  onSubmit?: (data) => SubmitPromise;
  onClick?: (id?: string) => () => void;
  onViewMalls: (id: string) => () => void;
  onClickCouponRedemption?: (id?: string) => () => void;
  userHasFeatureCouponAccess: boolean;
  userPermissions: User_userPermissions[];
}

const CouponsList: React.FC<CouponsListProps> = props => {
  const {
    onSubmit,
    coupons,
    disabled,
    settings,
    onClickCouponRedemption,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    userHasFeatureCouponAccess,
    userPermissions,
    onRowClick,
    onViewMalls
  } = props;

  const classes = useStyles(props);

  let NUMBER_OF_COLUMNS = 8;

  const couponsArray = Array.from(
    new Set(coupons.map(coupon => coupon.id))
  ).map(id => coupons.find(coupon => coupon.id === id));

  const userHasUpdateCouponAccess = Boolean(
    checkCouponPermissionAccess(userPermissions, "CHANGE_COUPON")
  );
  const userHasViewCouponCodesAccess = Boolean(
    checkCouponPermissionAccess(userPermissions, "VIEW_COUPON_REDEMPTION")
  );

  const handleColumnsCount = () => {
    if (userHasFeatureCouponAccess) {
      NUMBER_OF_COLUMNS += 1;
    }
  };

  React.useEffect(() => {
    handleColumnsCount();
  }, [userPermissions]);

  return (
    <ResponsiveTable>
      <TableHead colSpan={NUMBER_OF_COLUMNS} disabled={disabled} items={[]}>
        {userHasFeatureCouponAccess && (
          <TableCellHeader className={classes.colFeatured}>
            <FormattedMessage defaultMessage="Featured" />
          </TableCellHeader>
        )}
        <TableCellHeader className={classes.colWideHeaderCode}>
          <FormattedMessage defaultMessage="Coupon Code" />
        </TableCellHeader>{" "}
        <TableCellHeader className={classes.colWideHeader}>
          <FormattedMessage defaultMessage="Title" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideHeader}>
          <FormattedMessage defaultMessage="Redeem Status" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideHeader}>
          <FormattedMessage defaultMessage="Redemption" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideHeader}>
          <FormattedMessage defaultMessage="Effectivity Date(s)" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideHeaderMall}>
          <FormattedMessage defaultMessage="Mall" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideHeaderQty}>
          <FormattedMessage defaultMessage="Qty (pcs.)" />
        </TableCellHeader>
        <TableCellHeader className={classes.colWideIcon}></TableCellHeader>
      </TableHead>
      <TableFooter>
        <TableRow>
          <TablePagination
            colSpan={NUMBER_OF_COLUMNS}
            settings={settings}
            hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
            onNextPage={onNextPage}
            onUpdateListSettings={onUpdateListSettings}
            hasPreviousPage={
              pageInfo && !disabled ? pageInfo.hasPreviousPage : false
            }
            onPreviousPage={onPreviousPage}
          />
        </TableRow>
      </TableFooter>
      <TableBody>
        {renderCollection(
          couponsArray,
          (coupon, index) => (
            <TableRow
              className={classes.tableRow}
              hover={!!coupon}
              key={coupon ? coupon.id : "skeleton"}
              onClick={undefined}
            >
              {userHasFeatureCouponAccess && (
                <TableCell className={classes.colFeatured}>
                  <div onClick={e => e.stopPropagation()}>
                    <CouponFeaturedStar
                      id={coupon.id}
                      isFeatured={coupon.isFeatured}
                      onSubmit={onSubmit}
                    />
                  </div>
                </TableCell>
              )}
              <TableCell
                className={classes.colWide}
                data-test="couponCode"
                id={`couponCode${index}`}
              >
                <div className={classes.tableCodes}>
                  {coupon && coupon.code ? coupon.code : <Skeleton />}
                </div>
              </TableCell>
              <TableCell
                className={classes.colWide}
                data-test="couponTitle"
                id={`couponTitle${index}`}
              >
                {coupon && coupon.name ? (
                  <>
                    <Tooltip title={coupon.name}>
                      <div className={classes.tableTruncate}>
                        {coupon.name.length > 15
                          ? `${coupon.name.slice(0, 15)}...`
                          : coupon.name}
                      </div>
                    </Tooltip>
                    <Tooltip title={`Created by: ${coupon.assignee}`}>
                      <div className={classes.assignedToContainer}>
                        Created by: {coupon.assignee}
                      </div>
                    </Tooltip>
                  </>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell
                className={classes.colWide}
                data-test="couponRedeemStatus"
                id={`couponRedeemStatus${index}`}
              >
                <div className={classes.tableStatuses}>
                  {maybe(() => (
                    <Chip
                      label={CouponStatus[coupon.status]}
                      style={{ backgroundColor: statusColors[coupon.status] }}
                      icon={
                        <FiberManualRecordIcon
                          style={{ color: fiberIconColors[coupon.status] }}
                        />
                      }
                    />
                  ))}
                </div>
              </TableCell>
              <TableCell
                className={classes.colWide}
                data-test="couponRedeemType"
                id={`couponRedeemType${index}`}
              >
                {coupon && coupon.redeemType ? (
                  <>
                    <div className={classes.redeemStatus}>
                      {coupon.redeemType === CouponRedeemTypeEnum.REDEEM_LATER
                        ? "Claim Now, Redeem Later"
                        : coupon.redeemType === CouponRedeemTypeEnum.REDEEM_NOW
                        ? "Redeem Now"
                        : coupon.redeemType}
                    </div>
                    {coupon.userType && (
                      <div className={classes.redeemSubText}>
                        {coupon.userType}
                        {coupon.usageLimit && ` : ${coupon.usageLimit}`}
                      </div>
                    )}
                  </>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell
                className={`${classes.colWide} ${classes.couponDates}`}
                data-test="couponDates"
                id={`couponDates${index}`}
              >
                {coupon && coupon.startDate && coupon.endDate ? (
                  <>
                    Occurs on{" "}
                    <strong>
                      {
                        <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                          {coupon.startDate}
                        </Moment>
                      }
                    </strong>{" "}
                    Until{" "}
                    <strong>
                      {
                        <Moment format="MM/DD/YYYY" tz="Asia/Manila">
                          {coupon.endDate}
                        </Moment>
                      }
                    </strong>{" "}
                  </>
                ) : (
                  <Skeleton />
                )}
              </TableCell>
              <TableCell
                className={classNames(classes.colWide, classes.viewMallCell)}
                data-test="couponMalls"
                id={`couponMalls${index}`}
                onClick={onViewMalls(coupon.id)}
              >
                <FormattedMessage defaultMessage="View Malls" />
              </TableCell>
              <TableCell
                className={classes.colWide}
                data-test="couponQty"
                id={`couponQty${index}`}
              >
                {coupon && coupon.quantity ? coupon.quantity : <Skeleton />}
              </TableCell>
              <TableCell className={classes.colActionsDiv}>
                <div className={classes.colActions}>
                  {userHasViewCouponCodesAccess && (
                    <IconButton
                      onClick={onClickCouponRedemption(coupon.id)}
                      id={`couponRedemptionBtnRow${index}`}
                    >
                      <ListIcon style={{ color: "#0000FF" }} />
                    </IconButton>
                  )}
                  {userHasUpdateCouponAccess && (
                    <IconButton
                      onClick={onRowClick(coupon.id)}
                      id={`couponEditBtn${index}`}
                    >
                      <EditIcon style={{ color: "#0000FF" }} />
                    </IconButton>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ),
          () => (
            <TableRow>
              <TableCell colSpan={NUMBER_OF_COLUMNS}>
                <FormattedMessage defaultMessage="No coupons found" />
              </TableCell>
            </TableRow>
          )
        )}
      </TableBody>
    </ResponsiveTable>
  );
};

CouponsList.displayName = "CouponList";
export default CouponsList;
